// FBR IRIS API Integration Service
// Handles invoice submission and validation with FBR
// Toggle USE_PRODUCTION to switch between sandbox and production

const isDev = import.meta.env.DEV;
const USE_PRODUCTION = true; // Set to true for production FBR submissions

const FBR_CONFIG = {
    // Sandbox endpoints (for testing scenarios)
    sandboxPostUrl: isDev
        ? '/fbr-api/postinvoicedata_sb'
        : 'https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata_sb',
    sandboxValidateUrl: isDev
        ? '/fbr-api/validateinvoicedata_sb'
        : 'https://gw.fbr.gov.pk/di_data/v1/di/validateinvoicedata_sb',
    sandboxToken: 'ff66d84e-a333-3aba-92ea-b7d66cfa045b',

    // Production endpoints (for real invoices)
    productionPostUrl: isDev
        ? '/fbr-api/postinvoicedata'
        : 'https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata',
    productionToken: '72cf9f61-29aa-36f3-bbdf-2a3bc2a9073c',

    // Active config (auto-selected based on USE_PRODUCTION flag)
    get postUrl() { return USE_PRODUCTION ? this.productionPostUrl : this.sandboxPostUrl; },
    get validateUrl() { return this.sandboxValidateUrl; }, // Validate is sandbox-only
    get token() { return USE_PRODUCTION ? this.productionToken : this.sandboxToken; },
};

// ======================== FBR Invoice JSON Format (V2) ========================

export interface FBRInvoicePayload {
    invoiceType: string;
    invoiceDate: string; // yyyy-MM-dd
    sellerNTNCNIC: string;
    sellerBusinessName: string;
    sellerProvince: string;
    sellerAddress: string;
    buyerNTNCNIC: string;
    buyerBusinessName: string;
    buyerProvince: string;
    buyerAddress: string;
    buyerRegistrationType: string;
    invoiceRefNo: string;
    scenarioId: string;
    items: FBRInvoiceItem[];
}

export interface FBRInvoiceItem {
    hsCode: string;
    productDescription: string;
    rate: string; // e.g. "18%"
    uoM: string; // Unit of Measurement
    quantity: number;
    totalValues: number;
    valueSalesExcludingST: number;
    fixedNotifiedValueOrRetailPrice: number;
    salesTaxApplicable: number;
    salesTaxWithheldAtSource: number;
    extraTax: string | number;
    furtherTax: number;
    sroScheduleNo: string;
    fedPayable: number;
    discount: number;
    saleType: string;
    sroItemSerialNo: string;
}

export interface FBRResponse {
    Invoice_Id?: string;
    invoiceNumber?: string;
    dated?: string;
    validationResponse?: {
        statusCode: string;
        status: string;
        error: string;
        errorCode?: string;
        invoiceStatuses?: Array<{
            itemSNo: string;
            statusCode: string;
            status: string;
            error: string;
            errorCode: string;
        }>;
    };
    // fallback for our own caught errors
    isLocalError?: boolean;
    localErrorMessage?: string;
}

// ======================== Service Functions ========================

/**
 * Strip all non-digit characters from an NTN or CNIC value.
 * FBR requires: CNIC = 13 digits, NTN = 7 digits — no hyphens, spaces, or special chars.
 */
function sanitizeNTNCNIC(value: string): string {
    return value.replace(/\D/g, '');
}

/**
 * Format date for FBR API (yyyy-MM-dd)
 */
function formatFBRDate(dateInput?: string | Date): string {
    if (!dateInput) {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    if (typeof dateInput === 'string') {
        const trimmed = dateInput.trim();
        // If it's already in yyyy-MM-dd format (e.g. "2026-08-27" or "2026-08-27T...")
        const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (ymd) {
            return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
        }
        // If it's DD/MM/YYYY (e.g. "27/08/2026")
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
            const parts = trimmed.split('/');
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
        }
        // General parse fallback
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return trimmed;
    }

    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
        const year = dateInput.getFullYear();
        const month = String(dateInput.getMonth() + 1).padStart(2, '0');
        const day = String(dateInput.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const fallback = new Date();
    return `${fallback.getFullYear()}-${String(fallback.getMonth() + 1).padStart(2, '0')}-${String(fallback.getDate()).padStart(2, '0')}`;
}

/**
 * Map Tax Rate to string with percentage
 */
function mapTaxRate(rate: number): string {
    return `${Math.round(rate)}%`;
}

/**
 * Map POS unit of measurement to FBR-accepted UoM values.
 * FBR accepts specific UoM strings from its reference API.
 */
function mapUoM(uom: string): string {
    const uomMap: Record<string, string> = {
        'PCS': 'Numbers, pieces, units',
        'PIECES': 'Numbers, pieces, units',
        'UNITS': 'Numbers, pieces, units',
        'NOS': 'Numbers, pieces, units',
        'EA': 'Numbers, pieces, units',
        'EACH': 'Numbers, pieces, units',
        'SET': 'Numbers, pieces, units',
        'PAIR': 'Numbers, pieces, units',
        'PAIRS': 'Pairs',
        'DOZEN': 'Dozens',
        'DOZENS': 'Dozens',
        'PACK': 'Packs',
        'PACKS': 'Packs',
        'KG': 'KG',
        'KGS': 'KG',
        'KILOGRAM': 'KG',
        'CARAT': 'Carat',
        'LTR': 'Litre',
        'LITRE': 'Litre',
        'MTR': 'Metre',
        'METRE': 'Metre',
        'SQM': 'Square Metre',
        'CBM': 'Cubic Metre',
        'JOB': 'JOB',
    };
    return uomMap[uom.toUpperCase()] || 'Numbers, pieces, units';
}

/**
 * Determine Registration Type based on NTN/CNIC presence
 */
function determineRegistrationType(ntn?: string, cnic?: string): string {
    if ((ntn && ntn.length > 5) || (cnic && cnic.length > 10)) return "Registered";
    return "Unregistered"; // FBR typical naming convention
}

/**
 * Build the FBR invoice payload from our POS order data.
 * Uses validated field formats that match FBR IRIS API requirements.
 */
export function buildFBRPayload(order: {
    invoiceType?: "Sale Invoice" | "Debit/Credit Note" | "Sale Return";
    invoiceNumber: string;
    invoiceDate?: string | Date;
    posId: string;
    buyerNtn?: string;
    buyerCnic?: string;
    buyerName: string;
    buyerPhone?: string;
    items: Array<{
        code: string;
        name: string;
        quantity: number;
        hsCode?: string;
        uom: string;
        taxRate: number;
        unitPrice: number;
        totalAmount: number; // Inclusive of tax
        taxAmount: number;
        discount: number;
    }>;
    totalAmount: number;
    totalQuantity: number;
    totalSaleValue: number; // Exclusive of tax
    totalTax: number;
    discount: number;
    paymentMethod: string;
    sellerName: string;
    sellerNTN: string;
    sellerProvince: string;
    sellerAddress: string;
}): FBRInvoicePayload {
    const isRegistered = !!(order.buyerNtn && order.buyerNtn.replace(/\D/g, '').length >= 7);
    return {
        invoiceType: order.invoiceType || "Sale Invoice",
        invoiceDate: formatFBRDate(order.invoiceDate),
        sellerNTNCNIC: sanitizeNTNCNIC(order.sellerNTN || "4113099"),
        sellerBusinessName: order.sellerName || "DYNAMIC INDUSTRIAL SOLUTIONS",
        sellerProvince: order.sellerProvince || "Punjab",
        sellerAddress: order.sellerAddress || "Main Market, Pakistan",
        buyerNTNCNIC: sanitizeNTNCNIC(order.buyerNtn || order.buyerCnic || "0000000000000"),
        buyerBusinessName: order.buyerName || "Walk-in Customer",
        buyerProvince: "Punjab",
        buyerAddress: "N/A",
        buyerRegistrationType: determineRegistrationType(order.buyerNtn, order.buyerCnic),
        invoiceRefNo: order.invoiceNumber,
        scenarioId: isRegistered ? "SN001" : "SN002",
        items: order.items.map((item) => {
            const valueSalesExcludingST = Math.round(item.unitPrice * item.quantity * 100) / 100;
            const rate = item.taxRate;
            // FBR calculates tax as: valueSalesExcludingST * rate / 100 (to 2 decimals)
            const salesTaxApplicable = Math.round(valueSalesExcludingST * rate) / 100;
            const totalValues = Math.round((valueSalesExcludingST + salesTaxApplicable) * 100) / 100;
            return {
                hsCode: item.hsCode || "8448.5900",
                productDescription: item.name,
                rate: mapTaxRate(rate),
                uoM: mapUoM(item.uom),
                quantity: item.quantity,
                totalValues: totalValues,
                valueSalesExcludingST: valueSalesExcludingST,
                fixedNotifiedValueOrRetailPrice: 0,
                salesTaxApplicable: salesTaxApplicable,
                salesTaxWithheldAtSource: 0,
                extraTax: 0,
                furtherTax: 0,
                sroScheduleNo: "",
                fedPayable: 0,
                discount: item.discount,
                saleType: "Goods at standard rate (default)",
                sroItemSerialNo: "",
            };
        }),
    };
}

/**
 * Submit invoice to FBR API (uses production or sandbox based on USE_PRODUCTION flag)
 */
export async function submitToFBR(payload: FBRInvoicePayload): Promise<FBRResponse> {
    return _submitToFBR(payload, FBR_CONFIG.postUrl, FBR_CONFIG.token);
}

/**
 * Submit invoice to FBR Sandbox API (always uses sandbox — for scenario testing only)
 */
export async function submitToFBRSandbox(payload: FBRInvoicePayload): Promise<FBRResponse> {
    return _submitToFBR(payload, FBR_CONFIG.sandboxPostUrl, FBR_CONFIG.sandboxToken);
}

/**
 * Submit an FBR reversal/cancellation (typically 'Sale Return' or 'Credit Note')
 */
export async function submitFBRReversal(payload: FBRInvoicePayload): Promise<FBRResponse> {
    // Modify the payload type specifically for reversal
    const reversalPayload = {
        ...payload,
        invoiceType: "Sale Return"
    };
    return _submitToFBR(reversalPayload, FBR_CONFIG.postUrl, FBR_CONFIG.token);
}

// Export diagnostic info for troubleshooting
export function getFBRDiagnostics() {
    return {
        mode: USE_PRODUCTION ? 'PRODUCTION' : 'SANDBOX',
        isDev: isDev,
        postUrl: FBR_CONFIG.postUrl,
        token: FBR_CONFIG.token.substring(0, 8) + '...' + FBR_CONFIG.token.substring(FBR_CONFIG.token.length - 4),
        fullToken: FBR_CONFIG.token,
    };
}

async function _submitToFBR(payload: FBRInvoicePayload, url: string, token: string): Promise<FBRResponse> {
    try {
        console.log('========== FBR SUBMISSION DEBUG ==========');
        console.log('[FBR] Mode:', USE_PRODUCTION ? '🔴 PRODUCTION' : '🟡 SANDBOX');
        console.log('[FBR] isDev:', isDev);
        console.log('[FBR] URL:', url);
        console.log('[FBR] Token:', token.substring(0, 12) + '...');
        console.log('[FBR] Payload:', JSON.stringify(payload, null, 2));
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        // Read body as text first (can only read once)
        const rawText = await response.text();
        console.log('[FBR] HTTP Status:', response.status, response.statusText);
        console.log('[FBR] Response Headers:', JSON.stringify(Object.fromEntries(response.headers.entries())));
        console.log('[FBR] Raw Response Body:', rawText);
        console.log('==========================================');

        // Check if it's HTML (IP not whitelisted or API down)
        if (rawText.includes('<!doctype') || rawText.includes('<html')) {
            throw new Error('FBR returned an HTML page instead of JSON. Your IP may not be whitelisted, or the FBR API is down.');
        }

        // Try to parse as JSON
        let data: any;
        try {
            data = JSON.parse(rawText);
        } catch {
            throw new Error(`FBR returned non-JSON response: ${rawText.substring(0, 300)}`);
        }

        // Attach metadata for debugging
        data._debug = {
            url,
            httpStatus: response.status,
            httpStatusText: response.statusText,
            mode: USE_PRODUCTION ? 'PRODUCTION' : 'SANDBOX',
            tokenUsed: token.substring(0, 12) + '...',
        };

        // Return the parsed data — even if HTTP status is not 200,
        // FBR often returns validation errors inside a 200/400 JSON body
        return data as FBRResponse;
    } catch (error: any) {
        console.error('[FBR] Submission error:', error);
        const isCORS = error instanceof TypeError && error.message === 'Failed to fetch';
        return {
            isLocalError: true,
            localErrorMessage: isCORS
                ? 'CORS blocked: The browser cannot reach the FBR API directly. This is expected in production — a server-side proxy is needed. Contact your developer.'
                : (error.message || 'Failed to submit to FBR'),
            validationResponse: {
                statusCode: "LOCAL_ERR",
                status: "Error",
                error: error.message
            }
        };
    }
}

/**
 * Validate/retrieve invoice from FBR
 */
export async function validateFBRInvoice(invoiceNumber: string): Promise<any> {
    try {
        const response = await fetch(`${FBR_CONFIG.validateUrl}?InvoiceNumber=${invoiceNumber}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${FBR_CONFIG.token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`FBR Validate API returned ${response.status}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('FBR validation error:', error);
        return { error: error.message };
    }
}

// ======================== FBR Sandbox Scenarios ========================

export interface FBRScenario {
    id: string;
    name: string;
    description: string;
    invoiceType: string;
    buyerRegistrationType: string;
    saleType: string;
    rate: string;
    taxRate: number; // numeric for calculation
    fedPayable: number;
    sroScheduleNo: string;
    sroItemSerialNo: string;
    furtherTax: number;
    extraTax: string | number;
}

export const FBR_SCENARIOS: FBRScenario[] = [
    {
        id: "SN001",
        name: "Standard Rate → Registered Buyer",
        description: "Sale of goods at standard rate (18%) to a buyer who has a valid NTN (registered taxpayer).",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Registered",
        saleType: "Goods at standard rate (default)",
        rate: "18%",
        taxRate: 18,
        fedPayable: 0,
        sroScheduleNo: "",
        sroItemSerialNo: "",
        furtherTax: 0,
        extraTax: 0,
    },
    {
        id: "SN002",
        name: "Standard Rate → Unregistered Buyer",
        description: "Sale of goods at standard rate (18%) to a walk-in/unregistered buyer (CNIC only, no NTN).",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Goods at standard rate (default)",
        rate: "18%",
        taxRate: 18,
        fedPayable: 0,
        sroScheduleNo: "",
        sroItemSerialNo: "",
        furtherTax: 0,
        extraTax: 0,
    },
    {
        id: "SN005",
        name: "Reduced Rate Sale",
        description: "Sale of goods at a reduced tax rate (e.g., 1% or 5%) as notified by FBR.",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Goods at Reduced Rate",
        rate: "1%",
        taxRate: 1,
        fedPayable: 50.36,
        sroScheduleNo: "EIGHTH SCHEDULE Table 1",
        sroItemSerialNo: "82",
        furtherTax: 120,
        extraTax: "",
    },
    {
        id: "SN006",
        name: "Exempt Goods Sale",
        description: "Sale of goods that are exempt from sales tax (classified as exempt).",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Registered",
        saleType: "Exempt goods",
        rate: "Exempt",
        taxRate: 0,
        fedPayable: 50.36,
        sroScheduleNo: "6th Schd Table I",
        sroItemSerialNo: "100",
        furtherTax: 120,
        extraTax: "",
    },
    {
        id: "SN007",
        name: "Zero Rated Sale",
        description: "Sale of goods at zero-rated tax (exports, diplomatic sales, etc.).",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Goods at zero-rate",
        rate: "0%",
        taxRate: 0,
        fedPayable: 0,
        sroScheduleNo: "327(I)/2008",
        sroItemSerialNo: "1",
        furtherTax: 0,
        extraTax: 0,
    },
    {
        id: "SN016",
        name: "Processing / Conversion of Goods",
        description: "Toll manufacturing or processing/conversion of goods on behalf of another party.",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Processing/Conversion of Goods",
        rate: "5%",
        taxRate: 5,
        fedPayable: 0,
        sroScheduleNo: "",
        sroItemSerialNo: "",
        furtherTax: 0,
        extraTax: 0,
    },
    {
        id: "SN017",
        name: "Goods — FED Charged in ST Mode",
        description: "Sale of goods where Federal Excise Duty (FED) is also applicable and charged in Sales Tax mode.",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Goods (FED in ST Mode)",
        rate: "8%",
        taxRate: 8,
        fedPayable: 0,
        sroScheduleNo: "",
        sroItemSerialNo: "",
        furtherTax: 0,
        extraTax: 0,
    },
    {
        id: "SN018",
        name: "Services — FED Charged in ST Mode",
        description: "Services rendered or provided where Federal Excise Duty (FED) is charged in Sales Tax mode.",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Services (FED in ST Mode)",
        rate: "8%",
        taxRate: 8,
        fedPayable: 0,
        sroScheduleNo: "",
        sroItemSerialNo: "",
        furtherTax: 0,
        extraTax: 0,
    },
    {
        id: "SN019",
        name: "Services Rendered or Provided",
        description: "General services rendered or provided to a buyer (consulting, installation, repair, etc.).",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Services",
        rate: "5%",
        taxRate: 5,
        fedPayable: 0,
        sroScheduleNo: "ICTO TABLE I",
        sroItemSerialNo: "1(ii)(ii)(a)",
        furtherTax: 0,
        extraTax: 0,
    },
    {
        id: "SN024",
        name: "Goods Listed in SRO 297(1)/2023",
        description: "Sale of goods listed under SRO 297(1)/2023 notification. Requires SRO schedule and serial numbers.",
        invoiceType: "Sale Invoice",
        buyerRegistrationType: "Unregistered",
        saleType: "Goods as per SRO.297(|)/2023",
        rate: "25%",
        taxRate: 25,
        fedPayable: 0,
        sroScheduleNo: "297(I)/2023-Table-I",
        sroItemSerialNo: "12",
        furtherTax: 0,
        extraTax: 0,
    },
];

/**
 * Build a scenario-specific test payload for FBR Sandbox testing
 * Each scenario has the correct invoiceType, registrationType, rates, etc.
 */
export function buildScenarioPayload(scenarioId: string): FBRInvoicePayload {
    const scenario = FBR_SCENARIOS.find(s => s.id === scenarioId) || FBR_SCENARIOS[0];
    const isService = scenario.invoiceType === "Service Invoice";

    // Use registered buyer NTN for registered scenarios, CNIC for unregistered
    const isRegistered = scenario.buyerRegistrationType === "Registered";
    const buyerNTN = isRegistered ? "1497996" : "";
    const buyerCNIC = isRegistered ? "" : "1000000000000";

    // Per-scenario sample item overrides (from official FBR sample JSONs)
    const sampleOverrides: Record<string, Partial<FBRInvoiceItem> & { _qty?: number; _valExcl?: number; _totalVal?: number }> = {
        SN005: {
            hsCode: "0102.2930", uoM: "Numbers, pieces, units", productDescription: "product Description41",
            _qty: 1, _valExcl: 1000, _totalVal: 0,
            salesTaxWithheldAtSource: 50.23, discount: 56.36,
        },
        SN006: {
            hsCode: "0102.2930", uoM: "Numbers, pieces, units", productDescription: "product Description41",
            _qty: 1, _valExcl: 10, _totalVal: 0,
            salesTaxWithheldAtSource: 50.23, discount: 56.36,
        },
        SN007: {
            hsCode: "0101.2100", uoM: "Numbers, pieces, units", productDescription: "test",
            _qty: 100, _valExcl: 100, _totalVal: 0,
        },
        SN016: {
            hsCode: "0101.2100", uoM: "Numbers, pieces, units", productDescription: "test",
            _qty: 1, _valExcl: 100, _totalVal: 0,
        },
        SN017: {
            hsCode: "0101.2100", uoM: "Numbers, pieces, units", productDescription: "TEST",
            _qty: 1, _valExcl: 100, _totalVal: 0,
        },
        SN018: {
            hsCode: "0101.2100", uoM: "Numbers, pieces, units", productDescription: "TEST",
            _qty: 20, _valExcl: 1000, _totalVal: 0,
        },
        SN019: {
            hsCode: "0101.2900", uoM: "Numbers, pieces, units", productDescription: "TEST",
            _qty: 1, _valExcl: 100, _totalVal: 0,
        },
        SN024: {
            hsCode: "0101.2100", uoM: "Numbers, pieces, units", productDescription: "TEST",
            _qty: 123, _valExcl: 1000, _totalVal: 0,
        },
    };

    const ov = sampleOverrides[scenario.id];

    // Item values
    const unitPrice = isService ? 8000 : 5000;
    const quantity = ov?._qty ?? (isService ? 1 : 2);
    const valueSalesExcludingST = ov?._valExcl ?? unitPrice * quantity;
    const salesTax = ov?.salesTaxApplicable ?? Math.round(valueSalesExcludingST * (scenario.taxRate / 100));
    const totalValues = ov?._totalVal ?? valueSalesExcludingST + salesTax;

    const hsCode = ov?.hsCode ?? (isService ? "9984.0000" : "8448.5900");
    const uoM = ov?.uoM ?? (isService ? "JOB" : "KG");
    const productDescription = ov?.productDescription ?? (isService ? "POS Installation & Configuration Service" : "Bosch Impact Drill GSB-550");

    return {
        invoiceType: scenario.invoiceType,
        invoiceDate: formatFBRDate(),
        sellerNTNCNIC: sanitizeNTNCNIC("4113099"),
        sellerBusinessName: "DYNAMIC INDUSTRIAL SOLUTIONS",
        sellerProvince: "Punjab",
        sellerAddress: "Main Market, Pakistan",
        buyerNTNCNIC: sanitizeNTNCNIC(buyerNTN || buyerCNIC),
        buyerBusinessName: isRegistered ? "Ahmed Contractors (Pvt) Ltd" : "Walk-in Customer",
        buyerProvince: "Punjab",
        buyerAddress: isRegistered ? "Industrial Area, Faisalabad" : "N/A",
        buyerRegistrationType: scenario.buyerRegistrationType,
        invoiceRefNo: `${scenario.id}-${Date.now().toString(36).toUpperCase()}`,
        scenarioId: scenario.id,
        items: [
            {
                hsCode,
                productDescription,
                rate: scenario.rate,
                uoM,
                quantity,
                totalValues,
                valueSalesExcludingST,
                fixedNotifiedValueOrRetailPrice: ov?.fixedNotifiedValueOrRetailPrice ?? 0,
                salesTaxApplicable: salesTax,
                salesTaxWithheldAtSource: ov?.salesTaxWithheldAtSource ?? 0,
                extraTax: scenario.extraTax,
                furtherTax: scenario.furtherTax,
                sroScheduleNo: scenario.sroScheduleNo,
                fedPayable: scenario.fedPayable,
                discount: ov?.discount ?? 0,
                saleType: scenario.saleType,
                sroItemSerialNo: scenario.sroItemSerialNo,
            },
        ],
    };
}

/** Legacy alias — kept for backwards compatibility */
export function buildDemoPayload(scenarioId: string = "SN001"): FBRInvoicePayload {
    return buildScenarioPayload(scenarioId);
}

